import { useState, useEffect, useCallback } from 'react';
import { realtimeService } from '../services/realtimeService';
import { supabase } from '../lib/supabase';

interface EngagementData {
  created_at: string;
  clicked_links_total: number;
  opened_emails_total: number;
}

interface DashboardMetrics {
  totalOpens: number;
  clickThroughRate: number;
  openRate: number;
  engagementData: EngagementData[];
  lastSevenDaysOpens: number;
  sentEmails: number;
  totalUserEmails: number;
  linkVsEmails: number;
  emailOpensTrend: number;
}

const initialMetrics: DashboardMetrics = {
  totalOpens: 0,
  clickThroughRate: 0,
  openRate: 0,
  engagementData: [],
  lastSevenDaysOpens: 0,
  sentEmails: 0,
  totalUserEmails: 0,
  linkVsEmails: 0,
  emailOpensTrend: 0
};

export function useDashboardMetrics() {
  const [metrics, setMetrics] = useState<DashboardMetrics>(initialMetrics);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      
      // Busca os dados dos últimos 7 dias
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { data, error: queryError } = await supabase
        .from('actv_total')
        .select('*')
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: true });

      if (queryError) throw queryError;

      if (!data || data.length === 0) {
        console.warn('Nenhum dado encontrado na tabela actv_total');
        return;
      }

      // Pega o registro mais recente para as métricas totais
      const latestData = data[data.length - 1];

      // Processa os dados para o formato esperado
      const processedData: EngagementData[] = data.map(item => ({
        created_at: item.created_at,
        clicked_links_total: item.clicked_links_total || 0,
        opened_emails_total: item.opened_emails_total || 0
      }));

      // Calcula a tendência de aberturas de email (comparação com período anterior)
      const midPoint = Math.floor(data.length / 2);
      const recentAvg = data.slice(midPoint).reduce((acc, curr) => acc + curr.opened_emails_total, 0) / midPoint;
      const oldAvg = data.slice(0, midPoint).reduce((acc, curr) => acc + curr.opened_emails_total, 0) / midPoint;
      const trend = oldAvg > 0 ? ((recentAvg - oldAvg) / oldAvg) * 100 : 0;

      setMetrics({
        totalOpens: latestData.total_emails_abertos_total || 0,
        clickThroughRate: latestData.click_through_rate_total || 0,
        openRate: latestData.open_rate_total || 0,
        engagementData: processedData,
        lastSevenDaysOpens: processedData.reduce((sum, item) => sum + item.opened_emails_total, 0),
        sentEmails: latestData.sent_email_total || 0,
        totalUserEmails: latestData.total_user_emails || 0,
        linkVsEmails: latestData.link_vs_emails || 0,
        emailOpensTrend: trend
      });

    } catch (err) {
      console.error('Erro ao buscar métricas:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();

    // Configura subscription para atualizações em tempo real
    const unsubscribe = realtimeService.subscribeToUpdates('actv_total', () => {
      fetchMetrics();
    });

    return () => {
      unsubscribe();
    };
  }, [fetchMetrics]);

  return { metrics, loading, error, refresh: fetchMetrics };
}